import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
  isValid,
  parse,
  roundToNearestMinutes,
  startOfDay,
  Locale,
} from 'date-fns'
import { zonedTimeToUtc, toDate } from 'date-fns-tz'
import i18next from 'i18next'

import { PopOverItem } from 'components/PopOver/types'
import { DateFormat, Duration, LocaleCode } from 'types/global'

import { LANGUAGE, DATE_LOCALE_MAP } from './constants/locale'
import { parseISO } from 'date-fns'

export const getDateLocale = (localeCode?: LocaleCode) => {
  const ln = localeCode || localStorage.getItem('LANGUAGE_SELECTION')
  return ln ? DATE_LOCALE_MAP[ln] : DATE_LOCALE_MAP[LANGUAGE.en]
}

export const parseDate = (dtInput: string | Date | undefined | null): Date | null => {
  if (!dtInput) {
    return null
  }

  if (typeof dtInput === 'string') {
    const parsed = new Date(dtInput)
    if (isValid(parsed)) {
      return parsed
    }
    const parsed2 = toDate(dtInput)
    if (isValid(parsed2)) {
      return parsed2
    }
    return null
  }

  return dtInput
}

export const formatDatetime = (
  datetime?: Date | string | null,
  pattern?: DateFormat,
  isDateOnly?: boolean,
  locale?: Locale,
) => {
  if (!datetime) {
    return null
  }

  const date = typeof datetime === 'string' ? parseDate(datetime) : datetime
  if (!date || !isValid(date)) {
    return null
  }

  const isoDate = zonedTimeToUtc(date, Intl.DateTimeFormat().resolvedOptions().timeZone)
  const dtFormat = pattern ? pattern : isDateOnly ? 'PPP' : 'PPPp'

  return format(isoDate, dtFormat, { locale: locale ?? getDateLocale() }).toString()
}

const isISOString = (dateString: string) => {
  const date = parseISO(dateString)
  return isValid(date)
}

export const formatDatetimeFromParsedISO = (
  datetime?: Date | string | null,
  pattern?: DateFormat,
  isDateOnly?: boolean,
  locale?: Locale,
) => {
  // if passed datetime value is string, process like ISO string
  if (typeof datetime == 'string' && isISOString(datetime.replace(' ', 'T'))) {
    const parsedDateTime = parseISO(datetime.replace(' ', 'T'))

    return formatDatetime(parsedDateTime, pattern, isDateOnly, locale)
  }

  return formatDatetime(datetime, pattern, isDateOnly, locale)
}

export const getDateTimeWithTimezone: (date?: Date | string) => string = (date) =>
  formatDatetime(date, 'yyyy-MM-dd HH:mm:ssx') || '-'

const pHumanize: (datetime: string | Date, format?: DateFormat, dateOnly?: boolean) => string | string[] = (
  datetime,
  format,
  dateOnly = false,
) => {
  if (!datetime) {
    return '-'
  }

  const parsedDatetime = parseDate(datetime)

  if (!parsedDatetime) {
    return '-'
  }

  const now = new Date()
  const seconds = differenceInSeconds(now, parsedDatetime)

  // Show formatted value when datetime is in the future
  if (seconds < 0) {
    return formatDatetime(parsedDatetime, format, dateOnly) ?? '-'
  }

  if (seconds < 60) {
    return 'just_now_lbl'
  }

  const minutes = differenceInMinutes(now, parsedDatetime)

  if (minutes < 60) {
    return minutes == 1 ? 'minute_ago_lbl' : [String(minutes), 'min_ago_other']
  }

  const days = differenceInDays(now, startOfDay(parsedDatetime))
  const hours = differenceInHours(now, parsedDatetime)

  if (days === 0) {
    return dateOnly ? 'today_lbl' : [String(hours), hours === 1 ? 'hour_ago_one' : 'hour_ago_other']
  }

  if (days === 1) {
    return 'yesterday_lbl'
  }

  if (days >= 2 && days <= 7) {
    return [String(days), 'day_ago_other']
  }

  return formatDatetime(parsedDatetime, format, dateOnly) ?? '-'
}

export const humanize = (datetime: string | Date | null, format?: DateFormat, dateOnly?: boolean) => {
  if (!datetime) {
    return '-'
  }

  const result = pHumanize(datetime, format, dateOnly)

  if (typeof result === 'string') {
    return i18next.t(result)
  }

  if (Array.isArray(result)) {
    return i18next.t(result[1], { length: result[0] })
  }

  return '-'
}

export const toLocaleTime = (datetime: string) =>
  zonedTimeToUtc(datetime, Intl.DateTimeFormat().resolvedOptions().timeZone)

export const is12HourTimeFormat = (locale: string) => {
  const date = new Date()
  const dateString = date.toLocaleTimeString(locale, {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timeStyle: 'short',
  })

  if (dateString.match(/am|pm/i) || date.toString().match(/am|pm/i)) {
    return true
  }

  return false
}

export const is12HrByCurrentLocale = () => {
  const dateString = formatDatetime(new Date(), 'p') ?? ''

  if (dateString.match(/am|pm/i)) {
    return true
  }

  return false
}

export const generateTimePickerOptions = (is12?: boolean, is15minsGap = true) => {
  const is12HourFormat = is12 ? is12 : is12HrByCurrentLocale()
  return is12HourFormat ? generateTimePickerOptions12(is15minsGap) : generateTimePickerOptions24()
}

export const generateTimePickerOptions24 = () => {
  const options: PopOverItem<string>[] = []

  for (let i = 0; i < 24; i++) {
    const hour = String(i).padStart(2, '0')
    ;['00', '15', '30', '45'].forEach((m: string) => {
      const value = `${hour}:${m}`
      options.push({ label: value, value })
    })
  }

  return options
}

export const generateTimePickerOptions12hrNumber = () => {
  const options: PopOverItem<string>[] = []

  for (let i = 1; i <= 12; i++) {
    ;['00', '15', '30', '45'].forEach((m: string) => {
      const value = `${i}:${m}`
      options.push({ label: value, value })
    })
  }

  return options
}

export const generateTimePickerOptions12 = (min15Gap = true) => {
  const options: PopOverItem<string>[] = []

  for (let i = 0; i < 24; i++) {
    let hour = i
    const ampm = i < 12 ? 'AM' : 'PM'

    // Convert to 12-hour format and determine AM/PM
    if (hour === 0) {
      hour = 12
    } else if (hour >= 12) {
      if (hour > 12) {
        hour -= 12
      }
    }

    const hourValue = String(i).padStart(2, '0')
    const mins15 = ['00', '15', '30', '45']
    const mins30 = ['00', '30']
    ;(min15Gap ? mins15 : mins30).forEach((m: string) => {
      const label = `${hour}:${m} ${ampm}`
      const value = `${hourValue}:${m}`
      options.push({ label, value })
    })
  }

  return options
}

export const durationPickerOptions = [
  { label: '5 minutes', value: '5m', valueInMinutes: 5 },
  { label: '10 minutes', value: '10m', valueInMinutes: 10 },
  { label: '15 minutes', value: '15m', valueInMinutes: 15 },
  { label: '30 minutes', value: '30m', valueInMinutes: 30 },
  { label: '45 minutes', value: '45m', valueInMinutes: 45 },
  { label: '1 hour', value: '1h', valueInMinutes: 60 },
  { label: '1.5 hours', value: '1.5h', valueInMinutes: 90 },
  ...Array.from(Array(11)).map((_, index) => ({
    label: `${index + 2} hours`,
    value: `${String(index + 2)}h`,
    valueInMinutes: (index + 2) * 60,
  })),
  { label: '24 hours', value: '24h', valueInMinutes: 1440 },
]

export const computeDurationMinutes = (value: number, unit: Duration['unit']): number => {
  switch (unit) {
    case 'hour':
    case 'hours':
      return value * 60
    case 'minutes':
      return value
    default:
      return 0
  }
}

export const secToMinStr = (sec?: number | string | null, hideUnit?: boolean) => {
  if (!sec || isNaN(+sec) || +sec <= 0) return '-'

  if (+sec < 60) {
    return `${sec} ${hideUnit ? `` : `seconds`}`
  }
  const minutes = Math.floor(+sec / 60)
  const seconds = +sec % 60
  return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds} ${hideUnit ? `` : `minutes`}`
}

export const formatDurationFromSec = ({
  seconds,
  fallback = '-',
}: {
  seconds?: number | string | null
  fallback?: string
}): string | null => {
  if (!seconds || isNaN(Number(seconds))) return fallback

  const sec = Number(seconds)

  const hours = Math.floor(sec / 3600)
  const minutes = Math.floor((sec % 3600) / 60)
  const secs = sec % 60

  const parts = []
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`)

  return parts.join(' ')
}

export const secToMinDisplay = (sec: number) => {
  if (isNaN(sec)) return '-'

  const minute = Math.floor(sec / 60)
  const second = Math.round(sec % 60)
  const displaySec = second < 10 ? `0${second}` : second.toString()

  if (sec < 60) {
    return `${sec} seconds`
  }

  return `${minute}:${displaySec} minutes`
}

/**
 * Temp way to check if value is date or datetime
 */
export const isDatetime = (value: string) => value.includes(':') //TODO: Make this more comprehensive

export const parse12hr = (hour: string, meridiem: 'AM' | 'PM') => parse(`${hour} ${meridiem}`, 'hh:mm aa', new Date())

export const parse12hrWithPad = (hour: string, meridiem: 'AM' | 'PM') => {
  const [hStr, mStr] = hour.split(':')
  const h = parseInt(hStr, 10)
  const m = parseInt(mStr || '0', 10)

  const normalized = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  return parse(`${normalized} ${meridiem}`, 'hh:mm aa', new Date())
}

export const normalizeTimeZone = (time?: string | null): string => {
  if (!time) return ''
  const match = time.match(/^(\d{2}:\d{2}:\d{2})\s*([+-])(\d{2}):?(\d{2})?$/)
  if (!match) return time
  return `${match[1]}${match[2]}${match[3]}${match[4] || '00'}`
}

export const getNearest15Min = (dt: Date): Date => roundToNearestMinutes(dt, { nearestTo: 15 })

export const compareDate = (startDate: Date | string, endDate: Date | string) => {
  const d1 = new Date(startDate)
  const d2 = new Date(endDate)

  const equals = d1.getTime() === d2.getTime()

  if (!startDate || !endDate) return undefined

  if (equals) return '='

  if (d1 > d2) return '>'

  if (d1 < d2) return '<'
}
